import React, { useMemo, useState } from "react"
import Moment from "moment";
import { FileCsvIcon } from "@phosphor-icons/react";
import { UI_BODY_SEMIBOLD, UI_HEADLINE_SM, VideoInput, icons, colors2, UI_BODY_SM, UI_BODY_SEMIBOLD_SM, ImagePlaceholder, ButtonPrimaryV2, ButtonSecondaryV2, ButtonGhostV2, LoaderOverlay  } from "oolib";
import { StyledCoverSection, StyledCoverSectionV2, StyledCTA, StyledDescSection, StyledImage, StyledLeftSection, StyledLeftSectionV2, StyledMediaSection, StyledMediaSectionPlaceholder, StyledMetaDotV2, StyledMetaItemV2, StyledMetaSection, StyledMetaSectionV2, StyledRightSectionV2, StyledTitleSectionV2, StyledTPLActionsWrapper } from "./styled";
import { MetaPrimary } from "../../../../../../components/generalUI/V2_MetaComps/MetaPrimary";
import TplActions from "../../../../../../components/generalUI/TplActions";
import Tags__Intro from "../../../../../../components/generalUI/Tags__Intro";
import { useTheme } from "styled-components";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutPadding } from "@/CONSTANTS";
import { ImageInput } from "@/components/media/ImageInput";
import { useGetOnePublishedOrUnpublishedTpl } from "@/utils/react-query-hooks/tci";
import { useGetDataQuery } from "@/utils/react-query-hooks/getData";
import { SM_UIContent } from "@/_self_managed/SM_UIContent";
import { useBeginPublishingFlow } from "@/utils/contributionFlowUtils";
import { BreadcrumbHeader } from "@/components/UI/BreadcrumbHeader";

import { DirectDataImportModal } from "@/_self_managed/DirectDataImport/DirectDataImportModal";


const { Plus, User } = icons;
const { grey50, grey60, grey80, secondaryContainer } = colors2;

export const MemoPlaceholder  = React.memo(({ title }) => <ImagePlaceholder title={title} strechToFullHeight/>);

/**
 * v2 cover for the self managed dataset page. Replaces the <Breadcrumbs/> that
 * used to sit above <PublishedListing/> in SM_PublishedListing.
 *
 * Reads its own data off the route, since coverConfig only carries title/desc/btn.
 * The tpl query is already warm here (SM_PublishedListing fetches the same key).
 */
const CoverSectionV2 = ({ title }) => {
  const { contentType } = useParams();
  const navigate = useNavigate();

  const [showImportModal, setShowImportModal] = useState(false);

  const { data: tpl } = useGetOnePublishedOrUnpublishedTpl({ contentType });
  const { generateFn, generateTplIsLoading } = useBeginPublishingFlow();

  const { data: countRes } = useGetDataQuery({
    contentTypes: [contentType],
    useCountDAL: true,
    findQuery: {
      kp_published_status: { $in: ["published", "underModeration"] },
      kp_is_mod_clone: { $ne: true },
    },
    queryOptions: { enabled: !!contentType },
  });

  const documentCount = countRes?.data?.find(
    (d) => d.contentType === contentType
  )?.count;

  const uploadedOn =
    tpl?.dateCreated && Moment(tpl.dateCreated).format("DD MMM YYYY");

  /**
   * getTpl returns firstPublishedBy as an unpopulated ObjectId, so this stays
   * empty (and the slot stays hidden) until that ref gets populated.
   */
  const uploadedBy = tpl?.firstPublishedBy?.name;

  const metaItems = useMemo(
    () => [
      // source type isnt persisted on the tpl yet
      // { key: "sourceType", icon: FileCsvIcon, text: "Spreadsheet" },
      {
        key: "uploadedBy",
        icon: User,
        text: uploadedBy,
      },
      {
        key: "uploadedOn",
        text: uploadedOn && `Uploaded on ${uploadedOn}`,
      },
      {
        key: "documentCount",
        text: documentCount !== undefined && `${documentCount} Documents`,
      },
    ],
    [uploadedBy, uploadedOn, documentCount]
  );

  return (
    <StyledCoverSectionV2>
      <StyledTitleSectionV2>
        <ButtonGhostV2
          icon="CaretLeft"
          iconColor={grey80}
          onClick={() => navigate("/datasets")}
          style={{ padding: 4 }}
        />
        <StyledLeftSectionV2>
          <BreadcrumbHeader
            items={[
              { display: SM_UIContent.SM_TCI.display.datasets, to: "/datasets" },
              { display: title },
            ]}
          />

          <StyledMetaSectionV2>
            {/* only the slots that have a value render, so the dots between them
                never end up leading or trailing */}
            {metaItems
              .filter(({ text }) => text)
              .map(({ key, icon: Icon, text }, i) => (
                <React.Fragment key={key}>
                  {i > 0 && <StyledMetaDotV2>{"●"}</StyledMetaDotV2>}
                  <StyledMetaItemV2>
                    {Icon && <Icon size={16} color={grey60}/>}
                    {text}
                  </StyledMetaItemV2>
                </React.Fragment>
              ))}
          </StyledMetaSectionV2>
        </StyledLeftSectionV2>
      </StyledTitleSectionV2>

      <StyledRightSectionV2>
        {/* the create + navigate can take a beat — a silently dimmed button
            reads as a frozen page. Same treatment as the Choose Template
            drawer: full overlay while the doc is being created. */}
        {generateTplIsLoading && <LoaderOverlay />}
        <ButtonSecondaryV2
          icon={"Plus"}
          disabled={!tpl || generateTplIsLoading}
          onClick={() => generateFn(tpl)}
        >
          {"Add Data"}
        </ButtonSecondaryV2>
        <ButtonPrimaryV2 icon={"Plus"} onClick={() => setShowImportModal(true)}>
          {"Import More Data"}
        </ButtonPrimaryV2>
      </StyledRightSectionV2>

      <DirectDataImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        isImportMoreDataMode
        contentType={contentType}
      />
    </StyledCoverSectionV2>
  );
};

export const DesktopLayout = ({ content, title, desc, btn, coverImage,  coverVideo, metaPrimary, tagsIntro, isMobile, coverExists, showV2CoverUI }) => {
  const theme = useTheme()
  const navigate = useNavigate()

  if (showV2CoverUI) {
    return <CoverSectionV2 title={title}/>;
  }

  return(
    <StyledCoverSection
      isMobile={isMobile}
      style={{ padding: (title || desc) && LayoutPadding, paddingTop: "0" }}
    >  

      <StyledLeftSection>
        <StyledMetaSection>
          <div>
            {metaPrimary && (
                <div style={{ width: '100%' }}>
                  <MetaPrimary 
                    {...metaPrimary} 
                    content={content}
                    align={'left'}
                    />
                </div>   
            )}


            {title && <UI_HEADLINE_SM color={grey80}>{title}</UI_HEADLINE_SM>}
          </div>

          {tagsIntro ? (
              <Tags__Intro
              {...tagsIntro}
              content={content}
              align={'left'}
              style={{ padding: 0 }}
              />
            ) : null}

          

          {content?.meta && ( // positioned absolute at top right of Cover Section
              <StyledTPLActionsWrapper>
                <TplActions content={content} kebabStyle tplActionsIcon="DotsThree"/>
              </StyledTPLActionsWrapper>
          )}
        </StyledMetaSection>
        
      
        {desc && (
          <StyledDescSection>
              <UI_BODY_SEMIBOLD_SM color={grey80}>{'Description'}</UI_BODY_SEMIBOLD_SM>
              <UI_BODY_SM style={{ alignSelf: "flex-start", color: grey50 }}>{desc}</UI_BODY_SM>
          </StyledDescSection>
        )}
        
        {((btn?.action || btn?.to) && btn?.text) && (
          <div id="coverCTA"> {/*important for grid layout to work, so the button take entire space*/}
          <StyledCTA onClick={() => btn?.to ? navigate(btn.to) : btn.action()} desc={desc} coverExists={coverExists} theme={theme}>
              <Plus size={18} weight="bold" style={{ marginTop: "0.1rem" }}/>
              <UI_BODY_SEMIBOLD>
              {btn.text || "Publish"}
              </UI_BODY_SEMIBOLD>
          </StyledCTA>
          </div>
        )}
      
      </StyledLeftSection>

      <> 
        { 
          coverVideo?.[0]?.publicUrl ?  <StyledMediaSection><VideoInput value={coverVideo} readOnly={true}/></StyledMediaSection> 
          : 
          coverImage?.[0]?.publicUrl ?  <StyledMediaSection>
              {/* <StyledImage src={coverImage?.[0]?.publicUrl}/> */}
              <ImageInput
                value={coverImage}
                readOnly
                defaultImageSpread="cover"
                />
              </StyledMediaSection>
          : coverExists &&
          <StyledMediaSectionPlaceholder>
              <MemoPlaceholder title={title}/>
          </StyledMediaSectionPlaceholder> 
        }
      </>

    </StyledCoverSection>
    )
}